import React, { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import useAuth from "../../hooks/useAuth";

const variants = {
  personas: {
    title: "Aumenta la seguridad de tu cuenta",
    description:
      "Activa la autenticación multifactor para reforzar el acceso a tus productos financieros.",
    border: "border-[#a9caf5]",
    accent: "text-[#0042ab]",
    muted: "text-[#234f87]",
    button: "bg-[#0042ab] hover:bg-[#00388a] text-white",
    secondary: "text-[#0042ab]",
    badge: "bg-[#0042ab] text-white",
  },
  empresas: {
    title: "Protege a tu organización",
    description:
      "Configura MFA y añade una capa adicional de seguridad a las cuentas corporativas.",
    border: "border-[#c5b6ff]",
    accent: "text-[#5d00c5]",
    muted: "text-[#5f4da8]",
    button: "bg-[#7c4dff] hover:bg-[#5f3ad9] text-white",
    secondary: "text-[#5d00c5]",
    badge: "bg-[#5d00c5] text-white",
  },
};

const Steps = {
  REQUEST: "request",
  SCAN: "scan",
  CONFIRM: "confirm",
};

const copyToClipboard = async (value) => {
  if (!navigator?.clipboard) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    console.warn("No se pudo copiar al portapapeles", error);
    return false;
  }
};

const MfaConfigurator = ({ variant = "personas" }) => {
  const style = useMemo(() => variants[variant] || variants.personas, [variant]);
  const {
    enableMfa,
    confirmMfaSetup,
    cancelMfaSetup,
    hasMfa,
    hasPendingMfaSetup,
    isAuthenticated,
  } = useAuth();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [currentStep, setCurrentStep] = useState(() =>
    hasPendingMfaSetup ? Steps.CONFIRM : Steps.REQUEST,
  );

  const handleEnable = async () => {
    setStatus(null);
    setConfirmationCode("");
    setIsLoading(true);
    try {
      const result = await enableMfa();
      setConfig(result);
      setCurrentStep(Steps.SCAN);
      setStatus({
        type: "success",
        text: "Generamos un nuevo código secreto. Escanéalo y confirma la configuración ingresando el código de 6 dígitos antes de salir de esta pantalla.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        text:
          error.message ||
          "No fue posible solicitar MFA. Inténtalo nuevamente en unos minutos.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPendingMfaSetup) {
      setConfig(null);
      setConfirmationCode("");
      setCurrentStep(Steps.REQUEST);
    }
  }, [hasPendingMfaSetup]);

  if (!isAuthenticated || (hasMfa && !hasPendingMfaSetup)) return null;

  const copySecret = async (value) => {
    const ok = await copyToClipboard(value);
    if (!ok) {
      setStatus({
        type: "error",
        text: "No pudimos copiar el texto al portapapeles. Copia manualmente.",
      });
    } else {
      setStatus({
        type: "info",
        text: "Copiado al portapapeles.",
      });
    }
  };

  const badgeText = hasMfa
    ? "MFA activo"
    : hasPendingMfaSetup
    ? "Confirmación pendiente"
    : "MFA disponible";

  const handleCodeChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 6);
    setConfirmationCode(next);
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    if (confirmationCode.length < 6) {
      setStatus({
        type: "error",
        text: "Ingresa un código de 6 dígitos generado por tu app de autenticación.",
      });
      return;
    }
    setIsConfirming(true);
    setStatus(null);
    try {
      await confirmMfaSetup(confirmationCode);
      setConfig(null);
      setConfirmationCode("");
      setCurrentStep(Steps.REQUEST);
      setStatus({
        type: "success",
        text: "¡Listo! Activaste MFA. Usa tu app para generar códigos cuando inicies sesión.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        text:
          error.message ||
          "No pudimos confirmar el código. Verifica que sea el más reciente y vuelve a intentarlo.",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancelSetup = async () => {
    setIsCancelling(true);
    setStatus(null);
    try {
      await cancelMfaSetup();
      setConfig(null);
      setCurrentStep(Steps.REQUEST);
      setConfirmationCode("");
      setStatus({
        type: "info",
        text: "Se canceló la configuración pendiente. Tu MFA activo permanece sin cambios.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        text:
          error.message ||
          "No pudimos cancelar la configuración pendiente. Intenta nuevamente.",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleProceedToConfirm = () => {
    setStatus(null);
    setCurrentStep(Steps.CONFIRM);
  };

  const handleReturnToScan = () => {
    if (config) {
      setStatus(null);
      setCurrentStep(Steps.SCAN);
    }
  };

  return (
    <section
      className={`mt-10 rounded-3xl border bg-white/90 p-8 shadow-xl backdrop-blur ${style.border}`}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className={`text-2xl font-semibold ${style.accent}`}>
            {style.title}
          </h3>
          <p className={`mt-1 text-sm ${style.muted}`}>{style.description}</p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wide ${style.badge}`}
        >
          {badgeText}
        </span>
      </div>

      {currentStep === Steps.REQUEST && (
        <div className="mt-6 rounded-2xl bg-black/5 p-6">
          <p className={`text-sm font-semibold uppercase tracking-wide ${style.accent}`}>
            Generar código
          </p>
          <h4 className="mt-2 text-lg font-semibold text-gray-800">
            Obtén un nuevo secreto para tu app de autenticación.
          </h4>
          <p className={`mt-2 text-sm ${style.muted}`}>
            Al generarlo reemplazas cualquier configuración previa. Después podrás escanear el QR y confirmar con un código de 6 dígitos.
          </p>
          <button
            type="button"
            onClick={handleEnable}
            disabled={isLoading}
            className={`mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${style.button} ${
              isLoading ? "opacity-80" : ""
            }`}
          >
            {isLoading
              ? "Generando..."
              : hasMfa
              ? "Regenerar secreto"
              : "Generar código"}
          </button>
        </div>
      )}

      {currentStep === Steps.SCAN && config && (
        <div className="mt-6 space-y-4 rounded-2xl bg-black/5 p-6 text-sm">
          <div>
            <p className={`text-sm font-semibold uppercase tracking-wide ${style.accent}`}>
              Escanea el código QR
            </p>
            <p className={`mt-2 ${style.muted}`}>
              Usa Google Authenticator, Microsoft Authenticator o cualquier app TOTP para registrar este secreto.
            </p>
          </div>
          <div className="rounded-2xl bg-white px-4 py-4">
            <div className="flex flex-col items-center justify-center gap-3">
              <QRCode
                value={config.otpAuthUrl}
                size={224}
                bgColor="#ffffff"
                fgColor="#000000"
                style={{ width: "100%", maxWidth: "14rem", height: "auto" }}
              />
              <span className={`text-xs ${style.muted} text-center`}>
                Escanéalo y confirma que la cuenta aparezca en tu app antes de continuar.
              </span>
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-700">Código secreto</p>
            <div className="mt-1 flex items-center gap-2 break-all rounded-lg bg-white px-3 py-2 font-mono text-xs">
              <span>{config.secret}</span>
              <button
                type="button"
                onClick={() => copySecret(config.secret)}
                className={`ml-auto text-xs font-semibold ${style.secondary}`}
              >
                Copiar
              </button>
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-700">Enlace MFA</p>
            <a
              href={config.otpAuthUrl}
              target="_blank"
              rel="noreferrer"
              className={`block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs underline ${style.secondary}`}
            >
              {config.otpAuthUrl}
            </a>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleProceedToConfirm}
              className={`inline-flex flex-1 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${style.button}`}
            >
              Siguiente
            </button>
            <button
              type="button"
              onClick={handleCancelSetup}
              disabled={isCancelling}
              className="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isCancelling ? "Cancelando..." : "Cancelar"}
            </button>
          </div>
        </div>
      )}

      {currentStep === Steps.CONFIRM && (hasPendingMfaSetup || config) && (
        <div className="mt-6 space-y-4 rounded-2xl bg-black/5 p-6">
          <div>
            <p className={`text-sm font-semibold uppercase tracking-wide ${style.accent}`}>
              Introduce el código
            </p>
            <p className={`mt-2 text-sm ${style.muted}`}>
              Genera un código TOTP en tu app y escríbelo a continuación para activar MFA. Si necesitas revisar el QR, puedes volver atrás.
            </p>
          </div>
          <form className="flex flex-col gap-3" onSubmit={handleConfirm}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Ingresa el código de 6 dígitos"
              className="rounded-xl border border-transparent bg-white px-4 py-3 text-center text-lg font-semibold tracking-widest text-gray-800 shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400"
              value={confirmationCode}
              onChange={handleCodeChange}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="submit"
                disabled={isConfirming || confirmationCode.length < 6}
                className={`inline-flex flex-1 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${style.button} ${
                  isConfirming ? "opacity-80" : ""
                } disabled:cursor-not-allowed disabled:opacity-70`}
              >
                {isConfirming ? "Verificando..." : "Confirmar MFA"}
              </button>
              <button
                type="button"
                onClick={handleCancelSetup}
                disabled={isCancelling}
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isCancelling ? "Cancelando..." : "Cancelar pendiente"}
              </button>
            </div>
          </form>
          {config && (
            <button
              type="button"
              onClick={handleReturnToScan}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-semibold ${style.secondary} underline`}
            >
              Ver QR nuevamente
            </button>
          )}
        </div>
      )}

      {status && (
        <div
          className={`mt-6 rounded-2xl px-4 py-3 text-sm ${
            status.type === "error"
              ? "bg-red-100 text-red-700"
              : status.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-blue-100 text-blue-700"
          }`}
        >
          {status.text}
        </div>
      )}
    </section>
  );
};

export default MfaConfigurator;
